import { useState } from "react";
import { Ban, Check, ExternalLink, Plus, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useActualizeazaTargetareContact } from "@/hooks/api/mutate/useActualizeazaTargetareContact";
import { useSalveazaTargetareContact } from "@/hooks/api/mutate/useSalveazaTargetareContact";
import { useSalveazaTargetareExcludere } from "@/hooks/api/mutate/useSalveazaTargetareExcludere";
import { useStergeTargetareContact } from "@/hooks/api/mutate/useStergeTargetareContact";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { etichetaSursa } from "@/utils/targetareSurse";
import type {
  TargetareContact,
  TargetareExcludere,
  TargetareStareContact,
  TargetareTipContact,
} from "@/types/targetare";
import { formatDataOra } from "@/utils/targetareFormatting";

type ContacteFirmaProps = {
  cui: string;
  contacte: TargetareContact[];
  excluderi: TargetareExcludere[];
};

const TIPURI: { valoare: TargetareTipContact; eticheta: string }[] = [
  { valoare: "email", eticheta: "Email" },
  { valoare: "telefon", eticheta: "Telefon" },
  { valoare: "site", eticheta: "Site" },
  { valoare: "linkedin", eticheta: "LinkedIn" },
];

const STARE_CLASE: Record<TargetareStareContact, string> = {
  valid: "bg-ds-green-500 text-black hover:bg-ds-green-500",
  nevalidat: "bg-ds-text-400 text-navy-800 hover:bg-ds-text-400",
  invalid: "bg-ds-amber-500 text-black hover:bg-ds-amber-500",
  bounce: "bg-ds-amber-500 text-black hover:bg-ds-amber-500",
  opt_out: "bg-destructive text-destructive-foreground hover:bg-destructive",
};

const STARE_LABELS: Record<TargetareStareContact, string> = {
  valid: "valid",
  nevalidat: "nevalidat",
  invalid: "invalid",
  bounce: "bounce",
  opt_out: "opt-out",
};

function RandContact({
  contact,
  onStare,
  onSterge,
  ocupat,
}: {
  contact: TargetareContact;
  onStare: (stare: TargetareStareContact) => void;
  onSterge: () => void;
  ocupat: boolean;
}) {
  const [confirmaStergere, setConfirmaStergere] = useState(false);
  const verificare = contact.verificare ?? {};
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 text-sm">
      <Badge variant="outline" className="w-16 justify-center">
        {contact.tip}
      </Badge>
      <span className="font-mono text-xs">{contact.valoare}</span>
      <Badge className={cn("font-mono", STARE_CLASE[contact.stare])}>
        {STARE_LABELS[contact.stare]}
      </Badge>
      <span className="text-xs text-muted-foreground">
        {etichetaSursa(contact.sursa)}
        {"confirmat_de" in verificare &&
          ` · confirmat de ${etichetaSursa(String(verificare.confirmat_de))}`}
        {contact.incredere !== null && ` · încredere ${contact.incredere}%`}
        {"mx" in verificare && ` · MX ${verificare.mx ? "da" : "nu"}`}
        {contact.validat_la &&
          ` · verificat ${formatDataOra(contact.validat_la)}`}
      </span>
      {contact.dovada_url && (
        <a
          href={contact.dovada_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-ds-blue-600 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          dovadă
        </a>
      )}
      {contact.note && (
        <span className="basis-full text-xs text-muted-foreground">
          {contact.note}
        </span>
      )}
      <span className="ml-auto flex items-center gap-1">
        {contact.stare !== "valid" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={ocupat}
            onClick={() => onStare("valid")}
            title="Marchează valid"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
        {contact.stare !== "invalid" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={ocupat}
            onClick={() => onStare("invalid")}
            title="Marchează invalid"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        {contact.tip === "email" && contact.stare !== "opt_out" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            disabled={ocupat}
            onClick={() => onStare("opt_out")}
            title="Opt-out (nu mai contactăm această adresă)"
          >
            <Ban className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          size="sm"
          variant={confirmaStergere ? "destructive" : "ghost"}
          className="h-7 px-2"
          disabled={ocupat}
          onClick={() => {
            if (confirmaStergere) onSterge();
            setConfirmaStergere(!confirmaStergere);
          }}
          onBlur={() => setConfirmaStergere(false)}
          title={confirmaStergere ? "Apasă din nou pentru a șterge" : "Șterge"}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {confirmaStergere && <span className="ml-1 text-xs">Sigur?</span>}
        </Button>
      </span>
    </li>
  );
}

export function ContacteFirma({
  cui,
  contacte,
  excluderi,
}: ContacteFirmaProps) {
  const { toast } = useToast();
  const [tip, setTip] = useState<TargetareTipContact>("email");
  const [valoare, setValoare] = useState("");
  const [note, setNote] = useState("");
  const [confirmaExcludere, setConfirmaExcludere] = useState(false);
  const salveaza = useSalveazaTargetareContact();
  const actualizeaza = useActualizeazaTargetareContact();
  const sterge = useStergeTargetareContact();
  const excludere = useSalveazaTargetareExcludere();
  const ocupat =
    salveaza.isPending ||
    actualizeaza.isPending ||
    sterge.isPending ||
    excludere.isPending;

  const eroare = (titlu: string) => (e: Error) =>
    toast({ title: titlu, description: e.message, variant: "destructive" });

  const adauga = () => {
    const v = valoare.trim();
    if (!v) return;
    salveaza.mutate(
      { cui, tip, valoare: v, note: note.trim() || undefined, sursa: "manual" },
      {
        onSuccess: () => {
          setValoare("");
          setNote("");
        },
        onError: eroare("Contactul nu a putut fi salvat"),
      }
    );
  };

  const firmaExclusa = excluderi.some((e) => e.cui === cui && !e.email);

  return (
    <div className="space-y-3">
      {contacte.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Niciun contact colectat încă.
        </p>
      ) : (
        <ul className="space-y-1">
          {contacte.map((k) => (
            <RandContact
              key={k.id}
              contact={k}
              ocupat={ocupat}
              onStare={(stare) =>
                actualizeaza.mutate(
                  { id: k.id, cui, stare },
                  { onError: eroare("Starea nu a putut fi actualizată") }
                )
              }
              onSterge={() =>
                sterge.mutate(
                  { id: k.id, cui },
                  { onError: eroare("Contactul nu a putut fi șters") }
                )
              }
            />
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed p-2">
        <Select
          value={tip}
          onValueChange={(v) => setTip(v as TargetareTipContact)}
        >
          <SelectTrigger className="h-8 w-[130px]" aria-label="Tip contact">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIPURI.map((t) => (
              <SelectItem key={t.valoare} value={t.valoare}>
                {t.eticheta}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={valoare}
          onChange={(e) => setValoare(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") adauga();
          }}
          placeholder={
            tip === "email"
              ? "office@firma.ro"
              : tip === "telefon"
                ? "+40 ..."
                : "www.firma.ro"
          }
          className="h-8 min-w-[200px] flex-1 font-mono text-xs"
          aria-label="Valoare contact"
        />
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="notă (opțional)"
          className="h-8 min-w-[160px] flex-1"
          aria-label="Notă contact"
        />
        <Button
          size="sm"
          className="h-8"
          disabled={ocupat || !valoare.trim()}
          onClick={adauga}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Adaugă
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {firmaExclusa ? (
          <p className="text-sm text-destructive">
            Firma este exclusă din campanii:{" "}
            {excluderi
              .filter((e) => e.cui === cui)
              .map((e) => e.motiv)
              .join("; ")}
          </p>
        ) : (
          <Button
            size="sm"
            variant={confirmaExcludere ? "destructive" : "outline"}
            className="h-7"
            disabled={ocupat}
            onBlur={() => setConfirmaExcludere(false)}
            onClick={() => {
              if (confirmaExcludere) {
                excludere.mutate(
                  { cui, motiv: "opt-out firmă (manual)" },
                  { onError: eroare("Excluderea nu a putut fi salvată") }
                );
              }
              setConfirmaExcludere(!confirmaExcludere);
            }}
          >
            <Ban className="mr-1 h-3.5 w-3.5" />
            {confirmaExcludere
              ? "Confirmă excluderea firmei"
              : "Exclude firma din campanii"}
          </Button>
        )}
      </div>
    </div>
  );
}
